// Option-list rendering for the transport <select>s: the catalog picker (grouped by
// difficulty, labelled with cached/downloadable state) and the MIDI device chooser.
// Pure DOM over the data, kept out of controls.ts so that file stays under the cap.

import { isLoopbackPort, type MidiPort, shortMidiName } from "../midi/input";
import { type CatalogEntry, catalogOptionLabel } from "../songs/catalog";
import { el } from "./dom";

/** Rebuild the picker's grouped options (one optgroup per difficulty). */
export function renderCatalogOptions(
  catalog: HTMLSelectElement,
  entries: CatalogEntry[],
  cachedIds: Set<string>,
): void {
  catalog.replaceChildren(el("option", { value: "", textContent: "— Choose a song —" }));
  const byDifficulty = new Map<string, CatalogEntry[]>();
  for (const entry of entries) {
    const list = byDifficulty.get(entry.difficulty) ?? [];
    list.push(entry);
    byDifficulty.set(entry.difficulty, list);
  }
  for (const [difficulty, list] of byDifficulty) {
    const optgroup = el<HTMLOptGroupElement>("optgroup", { label: difficulty });
    for (const entry of list) {
      optgroup.append(
        el("option", {
          value: entry.id,
          textContent: catalogOptionLabel(entry, cachedIds.has(entry.id)),
        }),
      );
    }
    catalog.append(optgroup);
  }
}

/** Repaint existing option labels with current cached/downloadable state. */
export function repaintCatalogCache(
  catalog: HTMLSelectElement,
  entries: CatalogEntry[],
  cachedIds: Set<string>,
): void {
  for (const opt of catalog.querySelectorAll("option")) {
    const entry = entries.find((e) => e.id === opt.value);
    if (entry) opt.textContent = catalogOptionLabel(entry, cachedIds.has(entry.id));
  }
}

/** Fill the device chooser and reveal it only when 2+ real devices exist to pick
 *  between (otherwise auto-select + the 🎹 indicator cover it). */
export function renderDeviceOptions(
  device: HTMLSelectElement,
  deviceField: HTMLElement,
  ports: MidiPort[],
  selectedId: string | null,
): void {
  device.replaceChildren();
  const realCount = ports.filter((p) => !isLoopbackPort(p.name)).length;
  deviceField.style.display = realCount >= 2 ? "" : "none";
  if (ports.length === 0) {
    device.append(el("option", { value: "", textContent: "No MIDI devices" }));
    return;
  }
  for (const port of ports)
    device.append(el("option", { value: port.id, textContent: shortMidiName(port.name) }));
  if (selectedId) device.value = selectedId;
}
